import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { X } from 'lucide-react';
import type { CSSProperties } from 'react';
import type { SubscriptionTier } from '../../types/subscription';
import {
  NAV_ITEMS,
  shouldShowProBadge,
  PRO_BADGE_STYLE,
  TIER_BADGE_CONFIG,
  type NavItemDef,
} from './nav-config';

function ProBadge() {
  return <span style={PRO_BADGE_STYLE}>PRO</span>;
}

/* ---- MobileNavItem with hover tracking ---- */

interface MobileNavItemProps {
  item: NavItemDef;
  showBadge: boolean;
  onClose: () => void;
}

function MobileNavItem({ item, showBadge, onClose }: MobileNavItemProps) {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;

  const baseStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: 12,
    paddingLeft: 12,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'background-color 0.15s, color 0.15s',
    cursor: 'pointer',
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    borderLeft: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
  };

  return (
    <NavLink
      to={item.path}
      onClick={onClose}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={({ isActive }) => {
        if (isActive) {
          return {
            ...baseStyle,
            backgroundColor: 'rgba(99,102,241,0.1)',
            color: '#6366f1',
            borderLeft: '3px solid #6366f1',
            paddingLeft: 9,
          };
        }
        return {
          ...baseStyle,
          backgroundColor: hovered ? 'rgba(255,255,255,0.05)' : 'transparent',
          color: hovered ? '#ffffff' : 'rgba(255,255,255,0.7)',
        };
      }}
    >
      <Icon style={{ width: 20, height: 20, flexShrink: 0 }} />
      <span>{item.label}</span>
      {showBadge && <ProBadge />}
    </NavLink>
  );
}

/* ---- MobileNav ---- */

export interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier?: SubscriptionTier;
}

export function MobileNav({ isOpen, onClose, currentTier = 'free' }: MobileNavProps) {
  const [closeBtnHovered, setCloseBtnHovered] = useState(false);

  // Prevent body scroll when mobile nav is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  /* ---- Tier badge with proper colors ---- */
  const tierConfig = TIER_BADGE_CONFIG[currentTier];

  /* ---- Styles ---- */

  const wrapperStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
  };

  const backdropStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(4px)',
  };

  const panelStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    width: 256,
    backgroundColor: '#111118',
    boxShadow: '4px 0 25px rgba(0,0,0,0.5)',
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    height: 64,
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    padding: '0 16px',
  };

  const logoGroupStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  const logoIconStyle: CSSProperties = {
    display: 'flex',
    height: 32,
    width: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: '#6366f1',
  };

  const logoLetterStyle: CSSProperties = {
    fontSize: 14,
    fontWeight: 700,
    color: '#ffffff',
  };

  const brandTextStyle: CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    color: '#ffffff',
  };

  const brandAccentStyle: CSSProperties = {
    color: '#6366f1',
  };

  const closeBtnStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 8,
    border: 'none',
    backgroundColor: closeBtnHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
    color: closeBtnHovered ? '#ffffff' : 'rgba(255,255,255,0.7)',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
    padding: 0,
  };

  const navStyle: CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '16px 12px',
    overflowY: 'auto',
  };

  const tierAreaStyle: CSSProperties = {
    borderTop: '1px solid rgba(255,255,255,0.06)',
    padding: 16,
  };

  const tierInnerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const tierLabelStyle: CSSProperties = {
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
  };

  const tierBadgeStyle: CSSProperties = {
    borderRadius: 9999,
    backgroundColor: tierConfig.bg,
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: tierConfig.color,
    lineHeight: '16px',
  };

  return (
    // Keep md:hidden Tailwind for responsive visibility
    <div className="md:hidden" style={wrapperStyle}>
      {/* Backdrop */}
      <div
        style={backdropStyle}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-in sidebar */}
      <aside style={panelStyle}>
        {/* Header with close button */}
        <div style={headerStyle}>
          <div style={logoGroupStyle}>
            <div style={logoIconStyle}>
              <span style={logoLetterStyle}>C</span>
            </div>
            <span style={brandTextStyle}>
              CiteOps<span style={brandAccentStyle}>.ai</span>
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={closeBtnStyle}
            onMouseEnter={() => setCloseBtnHovered(true)}
            onMouseLeave={() => setCloseBtnHovered(false)}
            aria-label="Close menu"
          >
            <X style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {/* Navigation */}
        <nav style={navStyle}>
          {NAV_ITEMS.map((item) => {
            const showBadge = shouldShowProBadge(item.requiresTier, currentTier);
            return (
              <MobileNavItem
                key={item.path}
                item={item}
                showBadge={showBadge}
                onClose={onClose}
              />
            );
          })}
        </nav>

        {/* Tier info */}
        <div style={tierAreaStyle}>
          <div style={tierInnerStyle}>
            <span style={tierLabelStyle}>Current Plan</span>
            <span style={tierBadgeStyle}>{tierConfig.label}</span>
          </div>
        </div>
      </aside>
    </div>
  );
}
